// import { ApplicationInsights } from '@microsoft/applicationinsights-web'

// I think importing this directly should improve performance, as it tells RsPack to include the library in the bundle
// and should therefor be available immediately, rather than having to wait for the dynamic import
import { TrackJS } from 'trackjs'

import pkg from '../../../package.json'

/*
  Example configuration for monitoring plugin

  "monitoring": {
    "active": true,             // this is the default, so not needed but here for clarification
    "monitorLocalhost": true,   // this is false by default
    "trackjs": {
        "token": "a15a776ef76c4b91bd40a8d34c2bf30a",
        "application": "locusmol"
    },
    "applicationInsights": {
      // nothing here for now - do we want to include connectionString, disableAjaxTracking and disableFetchTracking ?
    }
  }
*/

const PKG_VERSION = pkg.version

/**
 * This is a non-standard plugin lifecycle function - created just for the monitoring so that we could "activate" (load) 3rd party monitoring
 * libraries as early in the application setup as possible - so this activate function is called from within app.js before the usual looping
 * through the plugin list and calling create.
 *
 * For both the current monitoring options (trackjs / applictaionInsights) there are bus messages to listen for, and that can't happen until
 * the create method - so that is why setup for these monitors is spread between activate (happens first) and create (happens later).
 *
 * @param {object} config The monitoring configuration object (see above for an example)
 */
async function activate (config) {
  if (location.host.includes('localhost') && !config.monitorLocalhost) // we don't usually want to include TrackJS in local development
    return

  if (config.active !== undefined && !config.active) // standard webEngine plugin config allows us to de-activate by setting active: false
    return

  if (config.trackjs)
    await activateTrackJS(config.trackjs)

  if (config.applicationInsights)
    await activateApplicationInsights(config.applicationInsights)
}

const activateTrackJS = async config => {
  TrackJS.install({ ...config, version: PKG_VERSION })
}

let appInsights // this gets defined here, but used later in finalizeSetupApplicationInsights
const activateApplicationInsights = async aiConfig => {
  // See https://ablcode.visualstudio.com/Atrius/_wiki/wikis/Wayfinder%20Webengine/86142/application-insights
  return (process.env.APP_INSIGHTS)
    ? import('@microsoft/applicationinsights-web')
      .then(({ ApplicationInsights }) => {
        appInsights = new ApplicationInsights({
          config: {
            connectionString: process.env.APP_INSIGHTS,
            disableAjaxTracking: true,
            disableFetchTracking: true,
            disableCookiesUsage: true, // older versions of app insights used to have this option, but it is now deprecated
            cookieCfg: {
              enabled: false // disable cookie usage per EU GDPR requirements
            }
          }
        })
        appInsights.loadAppInsights()
      })
    : null
}

function create (app, config) {
  if (location.host.includes('localhost') && !config.monitorLocalhost) // we don't usually want to include monitoring in local development
    return

  if (config.trackjs)
    finalizeSetupTrackjs(app, config.trackjs)

  if (config.applicationInsights)
    finalizeSetupApplicationInsights(app, config.applicationInsights)

  return {
    init: () => {} // noop
  }
}

function finalizeSetupTrackjs (app, trackjsConfig) {
  // set environment metadata
  if (location.host.includes('apps.locuslabs')) {
    if (location.pathname.includes('Commit')) {
      TrackJS.addMetadata('env', 'commit')
    } else {
      TrackJS.addMetadata('env', 'staging')
    }
  } else {
    TrackJS.addMetadata('env', 'production')
  }

  app.bus.on('venueData/venueDataLoaded', ({ venueData }) => {
    TrackJS.addMetadata('venueId', venueData.id)
    TrackJS.addMetadata('assetStage', venueData.assetStage)
    TrackJS.addMetadata('venueAssetVersion', venueData.version)
  })

  app.bus.on('kioskAdmin/kioskLocationChanged', ({ kioskLocation }) => {
    TrackJS.addMetadata('kioskLocation', kioskLocation)
  })
}

function finalizeSetupApplicationInsights (app, aiConfig) {
  if (process.env.APP_INSIGHTS) {
    app.bus.on('appInsights/log', (log) => {
      log.properties.location = typeof (window) !== 'undefined' ? window.location.origin : 'server'
      appInsights.trackEvent({ ...log })
      appInsights.flush()
    })
  }
}

export {
  create,
  activate
}
